import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { DashboardVM, TooltipCol } from '../lib/compute';

const VB_W = 800;

/** HTML overlay tooltip — real px size (readable on any screen), unlike SVG text
 *  which scales down with the viewBox. Measures its own width and clamps within the
 *  chart so it never spills past the edge (which would cause page overflow on mobile). */
function HtmlTip({ wrap, col }: { wrap: HTMLDivElement | null; col: TooltipCol }) {
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState<number>(-9999); // off-screen until measured
  const w = wrap ? wrap.clientWidth : 0;
  const x = (col.cx / VB_W) * w;
  useLayoutEffect(() => {
    const tw = tipRef.current?.offsetWidth ?? 0;
    let l = x + 12;
    if (l + tw > w - 4) l = x - 12 - tw; // flip leftward near the right edge
    l = Math.max(4, Math.min(l, Math.max(4, w - tw - 4))); // clamp inside the chart
    setLeft(l);
  }, [x, w, col]);
  return (
    <div ref={tipRef} className="cw-tip" style={{ left }}>
      {col.lines.map((ln, i) => (
        <div key={i} className={i === 0 ? 'cw-tip-title' : 'cw-tip-line'} style={i === 0 ? undefined : { color: ln.c }}>
          {ln.t}
        </div>
      ))}
    </div>
  );
}

/** Chart shell: renders the SVG with per-column hover hit areas + highlight band,
 *  and an HTML tooltip overlay. Chart-specific marks are passed as children.
 *  When `onSelect` is given, each column is clickable and `selected` draws a
 *  persistent accent band (e.g. the bucket whose data the right panel is showing). */
function ChartFrame({
  vbH, ariaLabel, cols, plotBottom, children, onSelect, selected,
}: { vbH: number; ariaLabel: string; cols: TooltipCol[]; plotBottom: number; children: ReactNode; onSelect?: (i: number) => void; selected?: number | null }) {
  const [hi, setHi] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const sel = selected ?? null;
  return (
    <div className="cw-chart" ref={wrapRef} onMouseLeave={() => setHi(null)}>
      <svg viewBox={`0 0 ${VB_W} ${vbH}`} className="cw-svg" role="img" aria-label={ariaLabel}>
        {children}
        {sel != null && cols[sel] && (
          <rect x={cols[sel].x} y="0" width={cols[sel].w} height={plotBottom} fill="#CF8B45" opacity="0.14" pointerEvents="none" />
        )}
        {hi != null && hi !== sel && cols[hi] && (
          <rect x={cols[hi].x} y="0" width={cols[hi].w} height={plotBottom} fill="#2B3136" opacity="0.045" pointerEvents="none" />
        )}
        {cols.map((c, i) => (
          <rect
            key={i}
            x={c.x}
            y="0"
            width={c.w}
            height={plotBottom}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHi(i)}
            onMouseMove={() => setHi(i)}
            onClick={onSelect ? () => onSelect(i) : undefined}
          />
        ))}
      </svg>
      {hi != null && cols[hi] && <HtmlTip wrap={wrapRef.current} col={cols[hi]} />}
    </div>
  );
}

/** Grouped revenue + spend bars with a ROAS line on a second axis. */
export function RevenueSpendChart({ chart, onSelect, selected }: { chart: DashboardVM['chart']; onSelect?: (i: number) => void; selected?: number | null }) {
  return (
    <ChartFrame vbH={320} ariaLabel="Umsatz, Werbeausgaben und ROAS" cols={chart.cols} plotBottom={296} onSelect={onSelect} selected={selected}>
      {chart.gridLines.map((g, i) => (
        <line key={i} x1="48" y1={g.y} x2="784" y2={g.y} stroke="#EEEAE4" strokeWidth="1" />
      ))}
      {chart.yLabels.map((yl, i) => (
        <text key={i} x="40" y={yl.y} textAnchor="end" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#A8A39D">{yl.label}</text>
      ))}
      {chart.yLabelsR.map((yr, i) => (
        <text key={i} x="792" y={yr.y} textAnchor="start" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#C49A6A">{yr.label}</text>
      ))}
      {chart.bars.map((b, i) => (
        <g key={i}>
          <rect x={b.sx} y={b.sy} width={b.w} height={b.sh} rx="2" fill="#D9D2C8" />
          <rect x={b.rx} y={b.ry} width={b.w} height={b.rh} rx="2" fill="#4A7FB5" />
          {b.showLabel && (
            <text x={b.cx} y="306" textAnchor="middle" fontFamily="DM Sans" fontSize="11" fill="#8A8580">{b.label}</text>
          )}
        </g>
      ))}
      <polyline points={chart.linePoints} fill="none" stroke="#E7A569" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {chart.roasDots.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r="3" fill="#FFFFFF" stroke="#E7A569" strokeWidth="2" />
      ))}
    </ChartFrame>
  );
}

/** Single metric (Umsatz / Werbeausgaben / Bestellungen / Ø Warenkorb) per period as bars. */
export function MetricBarChart({ chart, label, onSelect, selected }: { chart: NonNullable<DashboardVM['metricChart']>; label?: string; onSelect?: (i: number) => void; selected?: number | null }) {
  return (
    <ChartFrame vbH={320} ariaLabel={label ?? 'Metrik je Periode'} cols={chart.cols} plotBottom={296} onSelect={onSelect} selected={selected}>
      {chart.gridLines.map((g, i) => (
        <g key={i}>
          <line x1="48" y1={g.y} x2="784" y2={g.y} stroke="#EEEAE4" strokeWidth="1" />
          <text x="40" y={g.y} textAnchor="end" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#A8A39D">{g.label}</text>
        </g>
      ))}
      {chart.bars.map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="2" fill={chart.color} />
          {b.showLabel && (
            <text x={b.cx} y="306" textAnchor="middle" fontFamily="DM Sans" fontSize="11" fill="#8A8580">{b.label}</text>
          )}
        </g>
      ))}
    </ChartFrame>
  );
}

/** Umsatz bars (left € axis) + ROAS line (right × axis) per period, with the
 *  green ROAS target corridor. Shows the revenue LEVEL under each ROAS value. */
export function RoasWeekChart({ roasWeek }: { roasWeek: DashboardVM['roasWeek'] }) {
  return (
    <ChartFrame vbH={250} ariaLabel="Umsatz und ROAS je Periode" cols={roasWeek.cols} plotBottom={224}>
      <rect x="48" y={roasWeek.bandTop} width="736" height={roasWeek.bandH} fill="rgba(46,158,107,0.14)" />
      {roasWeek.gridLines.map((g, i) => (
        <line key={i} x1="48" y1={g.y} x2="784" y2={g.y} stroke="#EEEAE4" strokeWidth="1" />
      ))}
      {roasWeek.yLabels.map((yl, i) => (
        <text key={i} x="40" y={yl.y} textAnchor="end" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#A8A39D">{yl.label}</text>
      ))}
      {roasWeek.yLabelsR.map((yr, i) => (
        <text key={i} x="792" y={yr.y} textAnchor="start" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#C49A6A">{yr.label}</text>
      ))}
      {roasWeek.bars.map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="2" fill="#4A7FB5" />
          {b.showLabel && <text x={b.cx} y="236" textAnchor="middle" fontFamily="DM Sans" fontSize="11" fill="#8A8580">{b.label}</text>}
        </g>
      ))}
      <polyline points={roasWeek.linePoints} fill="none" stroke="#E7A569" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {roasWeek.roasDots.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r="3" fill="#FFFFFF" stroke="#E7A569" strokeWidth="2" />
      ))}
    </ChartFrame>
  );
}

/** 100 %-stacked ad-spend mix per period: each bar is one day / KW / month, the
 *  segments are the ad channels' share of that period's spend. Shows WHERE the
 *  marketing euros go over time, independent of the absolute level. */
export function SpendMixChart({ mix }: { mix: DashboardVM['spendMix'] }) {
  return (
    <ChartFrame vbH={250} ariaLabel="Werbekosten-Mix je Periode" cols={mix.cols} plotBottom={224}>
      {mix.yLabels.map((yl, i) => (
        <g key={i}>
          <line x1="48" y1={yl.y} x2="784" y2={yl.y} stroke="#EEEAE4" strokeWidth="1" />
          <text x="40" y={yl.y} textAnchor="end" dominantBaseline="middle" fontFamily="DM Sans" fontSize="11" fill="#A8A39D">{yl.label}</text>
        </g>
      ))}
      {mix.bars.map((b, i) => (
        <g key={i}>
          {b.segs.map((s, k) => (
            <rect key={k} x={b.x} y={s.y} width={b.w} height={s.h} fill={s.color} />
          ))}
          {b.showLabel && <text x={b.cx} y="236" textAnchor="middle" fontFamily="DM Sans" fontSize="11" fill="#8A8580">{b.label}</text>}
        </g>
      ))}
    </ChartFrame>
  );
}
